import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, FileText, Shield, User, Bot } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

export default function DisputeDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  useDocumentTitle("Dispute");

  const load = async () => {
    try {
      const { data } = await api.get(`/disputes/${id}`);
      setData(data);
    } catch (err) {
      toast.error("Could not load dispute");
      nav("/dashboard/disputes");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data) {
    return <DashboardShell><div className="text-[#252A34]/60">Loading…</div></DashboardShell>;
  }

  const d = data.dispute;
  const events = data.events || [];
  const media = data.media || [];
  const role = user?.id === d.customer_id ? "customer" : (user?.id === d.provider_user_id ? "provider" : "observer");

  const respond = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await api.post(`/disputes/${id}/respond`, { body: body.trim() });
      setBody("");
      toast.success("Response added to the timeline");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not respond");
    } finally { setSending(false); }
  };

  return (
    <DashboardShell>
      <div className="max-w-4xl">
        <button onClick={() => nav("/dashboard/disputes")} className="text-sm text-[#165a71] font-medium inline-flex items-center gap-2 mb-4" data-testid="dispute-back">
          <ArrowLeft className="h-4 w-4" /> Back to disputes
        </button>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 mb-6" data-testid="dispute-header">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#252A34]/60">{d.kind.replace(/_/g, " ")}</div>
              <h1 className="mt-1 font-display text-2xl font-semibold text-[#071b22]" data-testid="dispute-subject-heading">{d.subject}</h1>
              <div className="text-xs text-[#252A34]/60 mt-1">Opened by {d.opened_role} · {new Date(d.created_at).toLocaleString()}</div>
            </div>
            <div className="text-right">
              <span className="inline-block rounded-full bg-[#165a71]/10 text-[#165a71] text-xs font-medium px-3 py-1 uppercase tracking-wider" data-testid="dispute-status-pill">{d.status.replace(/_/g, " ")}</span>
              {d.reserved_amount > 0 && (
                <div className="mt-2 text-xs text-[#252A34]/70">Reserved <b>${d.reserved_amount.toFixed(2)}</b></div>
              )}
            </div>
          </div>

          <div className="mt-5 whitespace-pre-line text-sm text-[#252A34]">{d.description}</div>

          {(d.desired_resolution || d.desired_refund_amount) && (
            <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm">
              <div className="text-xs uppercase tracking-wider text-[#252A34]/60">Desired outcome</div>
              <div className="mt-1">{d.desired_resolution || "—"}</div>
              {d.desired_refund_amount ? <div className="text-xs mt-1">Refund requested: <b>${Number(d.desired_refund_amount).toFixed(2)}</b></div> : null}
            </div>
          )}

          {media.length > 0 && (
            <div className="mt-4">
              <div className="text-xs uppercase tracking-wider text-[#252A34]/60 mb-2">Evidence</div>
              <ul className="grid gap-2 sm:grid-cols-2" data-testid="dispute-media-list">
                {media.map((m) => (
                  <li key={m.id} className="rounded-xl border border-slate-200 p-3 flex items-center gap-3">
                    <FileText className="h-4 w-4 text-[#165a71] shrink-0" />
                    <a href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/files/${m.id}?auth=${encodeURIComponent(window.localStorage.getItem("gcw_bearer") || "")}`} target="_blank" rel="noreferrer" className="text-sm text-[#165a71] truncate">{m.original_filename}</a>
                    <span className="ml-auto text-xs text-[#252A34]/60">{Math.round((m.size_bytes || 0) / 1024)}kb</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-4 flex gap-3 flex-wrap">
            {d.booking_id && <Link to={`/dashboard/bookings/${d.booking_id}`} className="text-xs text-[#165a71] font-medium">View booking →</Link>}
            {d.order_id && <Link to={`/dashboard/orders/${d.order_id}`} className="text-xs text-[#165a71] font-medium">View order →</Link>}
          </div>
        </div>

        {/* Timeline */}
        <section className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-semibold text-[#071b22]">Timeline</h2>
            <div className="text-xs text-[#252A34]/60">{events.length} event(s)</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white divide-y" data-testid="dispute-timeline">
            {events.map((e) => (
              <div key={e.id} className="p-4 flex items-start gap-3" data-testid={`dispute-event-${e.id}`}>
                <div className={`h-8 w-8 shrink-0 rounded-lg flex items-center justify-center ${e.kind === "resolution" ? "bg-emerald-50 text-emerald-700" : e.kind === "status_change" ? "bg-[#165a71]/10 text-[#165a71]" : "bg-slate-100 text-[#252A34]/60"}`}>
                  {e.kind === "resolution" ? <Shield className="h-4 w-4" /> : e.actor_id ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wider text-[#252A34]/60">{e.kind.replace(/_/g, " ")}{e.to_status ? ` → ${e.to_status.replace(/_/g, " ")}` : ""}</div>
                  {e.body && <div className="text-sm text-[#252A34] mt-1 whitespace-pre-line">{e.body}</div>}
                  <div className="text-[10px] text-[#252A34]/50 mt-1">{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Respond box */}
        {["customer", "provider"].includes(role) && !d.resolved_at && (
          <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="dispute-respond">
            <div className="font-display font-semibold text-[#071b22] mb-2">Add to this conversation</div>
            <textarea
              rows={3}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Provide context, updates, or offer a resolution."
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#165a71]"
              data-testid="dispute-respond-input"
            />
            <div className="mt-2 flex justify-end">
              <button
                disabled={sending || !body.trim()}
                onClick={respond}
                className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-5 py-2 text-sm font-medium disabled:opacity-50"
                data-testid="dispute-respond-submit"
              >
                <Send className="h-4 w-4" /> {sending ? "Sending…" : "Post response"}
              </button>
            </div>
          </section>
        )}
      </div>
    </DashboardShell>
  );
}
